import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastProvider } from "@/components/ToastProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import { lazy, Suspense } from "react";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { AdminLayout } from "./layouts/AdminLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { OnboardingGuard } from "./components/OnboardingGuard";
import { AdminGuard } from "./components/AdminGuard";

// Fallback component
const PageLoader = () => (
  <div className="min-h-screen w-full bg-[#000000] flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-t-2 border-r-2 border-[#19F58C] animate-spin glow-mint"></div>
  </div>
);

const LandingPageV2 = lazy(() => import("./pages/LandingPageV2"));
const LandingWrapper = lazy(() => import("./components/landing_v0/landing-wrapper").then(m => ({ default: m.LandingWrapper })));
const LandingPage = lazy(() => import("./pages/landing/Index"));
const Login = lazy(() => import("./pages/auth").then(m => ({ default: m.Login })));
const Register = lazy(() => import("./pages/auth").then(m => ({ default: m.Register })));
const ForgotPassword = lazy(() => import("./pages/auth").then(m => ({ default: m.ForgotPassword })));
const PremiumLogin = lazy(() => import("./pages/auth/PremiumLogin").then(m => ({ default: m.PremiumLogin })));
const RegisterSuccess = lazy(() => import("./pages/auth/RegisterSuccess").then(m => ({ default: m.RegisterSuccess })));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword").then(m => ({ default: m.ResetPassword })));
const EmailConfirmation = lazy(() => import("./pages/auth/EmailConfirmation").then(m => ({ default: m.EmailConfirmation })));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback").then(m => ({ default: m.AuthCallback })));

const Overview = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Overview })));
const Conversations = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Conversations })));
const Settings = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Settings })));
const Leads = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Leads })));
const Analytics = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Analytics })));
const Automation = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Automation })));
const LeadDetails = lazy(() => import("./pages/dashboard").then(m => ({ default: m.LeadDetails })));
const CRM = lazy(() => import("./pages/dashboard").then(m => ({ default: m.CRM })));
const Funnel = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Funnel })));
const Team = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Team })));
const Reports = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Reports })));
const Churn = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Churn })));
const Billing = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Billing })));
const AITraining = lazy(() => import("./pages/dashboard").then(m => ({ default: m.AITraining })));
const BusinessConfigHub = lazy(() => import("./pages/dashboard").then(m => ({ default: m.BusinessConfigHub })));
const ForecastDashboard = lazy(() => import("./pages/dashboard").then(m => ({ default: m.ForecastDashboard })));
const MLDashboard = lazy(() => import("./pages/dashboard").then(m => ({ default: m.MLDashboard })));
const KnowledgeBase = lazy(() => import("./pages/dashboard").then(m => ({ default: m.KnowledgeBase })));

const Support = lazy(() => import("./pages/support/Support"));
const KanbanV2 = lazy(() => import("./pages/dashboard/KanbanV2").then(m => ({ default: m.KanbanV2 })));
const Sales = lazy(() => import("./pages/dashboard/Sales").then(m => ({ default: m.Sales })));
const PipelineSettings = lazy(() => import("./pages/dashboard/PipelineSettings").then(m => ({ default: m.PipelineSettings })));
const CompanySetup = lazy(() => import("./pages/setup").then(m => ({ default: m.CompanySetup })));
const OnboardingPage = lazy(() => import("./pages/onboarding").then(m => ({ default: m.OnboardingPage })));

const AdminDashboard = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminDashboard })));
const AdminClients = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminClients })));
const AdminAlerts = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminAlerts })));
const AdminLogs = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminLogs })));
const AdminLimits = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminLimits })));
const AdminSubscriptions = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminSubscriptions })));
const AdminApiKeys = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminApiKeys })));
const AdminSettings = lazy(() => import("./pages/admin/Settings").then(m => ({ default: m.AdminSettings })));
const NotFound = lazy(() => import("./pages/NotFound"));


const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } } });

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ToastProvider />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingWrapper />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/landing-v2" element={<LandingPageV2 />} />
            <Route path="/plans" element={<Navigate to="/register" replace />} />

            {/* Auth Routes */}
            <Route path="/login" element={<PremiumLogin />} />
            <Route path="/premium-login" element={<PremiumLogin />} />
            <Route path="/signup" element={<Navigate to="/register" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-success" element={<RegisterSuccess />} />
            <Route path="/checkout" element={<Navigate to="/register" replace />} />
            <Route path="/checkout-success" element={<Navigate to="/register" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Setup Routes — Protected (C15 fix) */}
            <Route path="/setup/company" element={<ProtectedRoute><CompanySetup /></ProtectedRoute>} />

            {/* Onboarding Route */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Overview />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/conversations"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Conversations />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/leads"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Leads />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/analytics"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Analytics />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/automation"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Automation />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/kanban"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <KanbanV2 />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/pipeline/config"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <PipelineSettings />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/leads/:id"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <LeadDetails />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/crm"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <CRM />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/funnel"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Funnel />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/sales"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Sales />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/team"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Team />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/reports"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Reports />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/churn"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Churn />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Billing />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/ai-training"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <AITraining />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/business-config"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <BusinessConfigHub />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/forecast"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <ForecastDashboard />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/ml"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <MLDashboard />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/support"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <Support />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/knowledge"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DashboardLayout>
                      <KnowledgeBase />
                    </DashboardLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminClients />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/alerts"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminAlerts />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminLogs />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/limits"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminLimits />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminSubscriptions />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminSettings />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/api-keys"
              element={
                <ProtectedRoute>
                  <AdminGuard>
                    <AdminLayout>
                      <AdminApiKeys />
                    </AdminLayout>
                  </AdminGuard>
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
